import React, { useState } from 'react';
import { Camera, Check, GripVertical, Pencil, Trash2, X } from 'lucide-react';
import type { SurveyNoteCard } from '@officehub/shared';
import { AutoGrowTextArea } from '../inputs/AutoGrowTextArea';
import type { NoteEditorProps, RenderNoteEditor } from './types';
import { splitNoteText, MAX_NOTE_CARD_TEXT_LENGTH } from './noteTextSplit';
import { newClientId } from '../ids/newClientId';

export interface NoteCardProps {
  card: SurveyNoteCard;
  /**
   * Commit this card's edit. Takes an array because a save whose text exceeds
   * `MAX_NOTE_CARD_TEXT_LENGTH` splits into multiple sibling cards instead of
   * being rejected outright (SALES-3) -- the common case is still a
   * single-element array, same as a plain in-place update.
   */
  onUpdate: (updated: SurveyNoteCard[]) => void;
  onDelete: (cardId: string) => void;
  onDragStart: (e: React.DragEvent, cardId: string) => void;
  onDragOver: (e: React.DragEvent, cardId: string) => void;
  onDrop: (e: React.DragEvent, cardId: string) => void;
  isDraggingOver: boolean;
  /**
   * Optional editor injector. When omitted, the built-in auto-grow textarea
   * with voice dictation is used. The main client injects its AI-polish editor
   * here so notes keep polish without this package importing app code.
   */
  renderEditor?: RenderNoteEditor;
  /**
   * Commit the note when the editor loses focus (in addition to the explicit
   * Save button and Cmd/Ctrl+Enter). Field apps enable this so a typed note is
   * never lost by tapping the page-level Save without tapping the card's Save.
   * Leave off when injecting an editor that opens a popover/modal (its own
   * controls would blur the field and commit prematurely).
   */
  commitOnBlur?: boolean;
}

const EDITOR_PLACEHOLDER = 'What did you observe? Tap the mic to dictate.';

export function NoteCard({
  card,
  onUpdate,
  onDelete,
  onDragStart,
  onDragOver,
  onDrop,
  isDraggingOver,
  renderEditor,
  commitOnBlur = false,
}: NoteCardProps): React.ReactElement {
  // A brand-new (empty) card opens straight into edit mode so the rep can
  // write one note and save it — the "write and save one at a time" pattern.
  const [editing, setEditing] = useState(card.text === '');
  const [draft, setDraft] = useState(card.text);

  const startEdit = () => {
    setDraft(card.text);
    setEditing(true);
  };

  const saveEdit = () => {
    const trimmed = draft.trim();
    if (!trimmed) {
      // Saving an empty card discards it, so an abandoned "Add note" tap
      // doesn't leave a blank card behind.
      onDelete(card.cardId);
      setEditing(false);
      return;
    }

    // The server hard-rejects any single card's text past this length
    // (invalid-argument), which used to fail the WHOLE save and silently
    // skip the photo-upload loop that ran after it (SALES-3). Split instead
    // of rejecting: the first chunk keeps this card's identity, the rest
    // become new sibling cards.
    const chunks = splitNoteText(trimmed, MAX_NOTE_CARD_TEXT_LENGTH);
    const now = new Date().toISOString();
    const updated: SurveyNoteCard[] = chunks.map((text, i) =>
      i === 0
        ? { ...card, text, updatedAt: now }
        : { cardId: newClientId(), order: card.order, text, createdAt: now, updatedAt: now },
    );
    onUpdate(updated);
    setEditing(false);
  };

  const cancelEdit = () => {
    // Cancelling a never-saved (empty) card removes it entirely.
    if (card.text === '') {
      onDelete(card.cardId);
      return;
    }
    setDraft(card.text);
    setEditing(false);
  };

  const editorProps: NoteEditorProps = {
    value: draft,
    onChange: setDraft,
    autoFocus: true,
    placeholder: EDITOR_PLACEHOLDER,
    onKeyDown: (e) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) saveEdit();
      if (e.key === 'Escape') cancelEdit();
    },
    // Save/Cancel/mic controls all preventDefault on mousedown to keep focus,
    // so a real blur means focus left the card entirely — commit the note then.
    onBlur: commitOnBlur ? () => saveEdit() : undefined,
  };

  return (
    <div
      draggable={!editing}
      onDragStart={(e) => onDragStart(e, card.cardId)}
      onDragOver={(e) => onDragOver(e, card.cardId)}
      onDrop={(e) => onDrop(e, card.cardId)}
      className={`flex gap-2 rounded-2xl border bg-white p-3 shadow-sm transition-colors ${
        isDraggingOver ? 'border-[#007ACC] bg-blue-50' : 'border-slate-200'
      }`}
    >
      {/* Drag handle (reordering only makes sense once a card is saved) */}
      {!editing && (
        <button
          type="button"
          className="flex-none cursor-grab touch-none pt-0.5 text-slate-300 active:cursor-grabbing"
          aria-label="Drag to reorder"
          style={{ minWidth: 20 }}
        >
          <GripVertical size={18} />
        </button>
      )}

      {/* Content */}
      <div className="min-w-0 flex-1">
        {editing ? (
          <div className="flex flex-col gap-2">
            {renderEditor ? renderEditor(editorProps) : (
              <AutoGrowTextArea
                autoFocus
                value={draft}
                onValueChange={setDraft}
                placeholder={EDITOR_PLACEHOLDER}
                onKeyDown={editorProps.onKeyDown}
                onBlur={editorProps.onBlur}
              />
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={saveEdit}
                className="flex min-h-[44px] items-center gap-1 rounded-xl bg-[#007ACC] px-3 text-sm font-medium text-white"
              >
                <Check size={14} /> Save note
              </button>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={cancelEdit}
                className="flex min-h-[44px] items-center gap-1 rounded-xl border border-slate-300 px-3 text-sm text-slate-600"
              >
                <X size={14} /> Cancel
              </button>
            </div>
          </div>
        ) : (
          <p className="whitespace-pre-wrap text-sm text-slate-800">{card.text}</p>
        )}
        {card.photoId && !editing && (
          <div className="mt-1 flex items-center gap-1 text-xs text-slate-400">
            <Camera size={12} />
            <span>Photo attached</span>
          </div>
        )}
      </div>

      {/* Actions */}
      {!editing && (
        <div className="flex flex-none flex-col gap-1">
          <button
            type="button"
            onClick={startEdit}
            aria-label="Edit note"
            className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-xl text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <Pencil size={15} />
          </button>
          <button
            type="button"
            onClick={() => onDelete(card.cardId)}
            aria-label="Delete note"
            className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-xl text-slate-400 hover:bg-red-50 hover:text-red-600"
          >
            <Trash2 size={15} />
          </button>
        </div>
      )}
    </div>
  );
}
